import React, { useEffect, useState } from 'react';
import { Material, CreateMaterialRequest } from '../../types/material';
import styles from './CatalogPage.module.css';

interface CatalogService {
  list: (page?: number, perPage?: number) => Promise<{ data: Material[]; total: number; page: number; totalPages: number }>;
  create: (data: CreateMaterialRequest) => Promise<Material>;
  delete: (id: number) => Promise<void>;
}

interface CatalogPageProps {
  title: string;
  subtitle: string;
  listTitle: string;
  listDescription: string;
  placeholder: string;
  service: CatalogService;
}

const TrashIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.actionIcon}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const CatalogPage: React.FC<CatalogPageProps> = ({
  title,
  subtitle,
  listTitle,
  listDescription,
  placeholder,
  service,
}) => {
  const [items, setItems] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formNome, setFormNome] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 20;

  const loadItems = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const result = await service.list(page, perPage);
      setItems(result.data);
      setTotal(result.total);
      setCurrentPage(result.page);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar itens.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems(1);
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    setSuccess('');

    const nome = formNome.trim().toUpperCase();
    if (!nome) {
      setFormError('Informe o nome.');
      return;
    }

    setSubmitting(true);
    try {
      await service.create({ nome });
      setSuccess(`"${nome}" cadastrado com sucesso.`);
      setFormNome('');
      await loadItems(1);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Falha ao cadastrar.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item: Material) => {
    if (!window.confirm(`Deseja realmente excluir "${item.nome}"?`)) return;

    setDeletingId(item.id);
    try {
      await service.delete(item.id);
      await loadItems(currentPage);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Falha ao excluir.');
    } finally {
      setDeletingId(null);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      loadItems(page);
    }
  };

  const firstIndex = (currentPage - 1) * perPage + 1;
  const lastIndex = Math.min(currentPage * perPage, total);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
      </div>

      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Cadastrar novo item</h2>
        <p className={styles.sectionDescription}>
          O nome será convertido para maiúsculas automaticamente.
        </p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inlineField}>
            <div className={styles.field}>
              <input
                className={styles.input}
                type="text"
                value={formNome}
                onChange={(e) => setFormNome(e.target.value)}
                placeholder={placeholder}
                maxLength={100}
                autoFocus
              />
            </div>
            <button className={styles.primaryButton} type="submit" disabled={submitting}>
              {submitting ? 'Cadastrando...' : 'Cadastrar'}
            </button>
          </div>
          {formError ? <div className={`${styles.message} ${styles.error}`}>{formError}</div> : null}
          {success ? <div className={`${styles.message} ${styles.success}`}>{success}</div> : null}
        </form>
      </div>

      <div className={styles.card} style={{ marginTop: 24 }}>
        <div className={styles.listHeader}>
          <div>
            <h2 className={styles.sectionTitle}>{listTitle}</h2>
            <p className={styles.sectionDescription}>{listDescription}</p>
          </div>
          <button className={styles.secondaryButton} type="button" onClick={() => loadItems(currentPage)} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar lista'}
          </button>
        </div>

        {error ? <div className={`${styles.message} ${styles.error}`}>{error}</div> : null}

        {loading ? <p className={styles.emptyState}>Carregando itens...</p> : null}

        {!loading && !items.length ? (
          <p className={styles.emptyState}>Nenhum item cadastrado.</p>
        ) : null}

        {!loading && items.length ? (
          <>
            <div className={styles.paginationInfo}>
              Mostrando {firstIndex}–{lastIndex} de {total} itens
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Cadastrado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className={styles.itemName}>{m.nome}</div>
                      </td>
                      <td>
                        <div className={styles.itemMeta}>
                          {new Date(m.createdAt).toLocaleDateString('pt-BR')}
                        </div>
                      </td>
                      <td>
                        <button
                          className={styles.deleteButton}
                          type="button"
                          onClick={() => handleDelete(m)}
                          disabled={deletingId === m.id}
                          aria-label="Excluir"
                          title="Excluir"
                        >
                          {deletingId === m.id ? '...' : <TrashIcon />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageButton}
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Anterior
                </button>
                <span className={styles.pageInfo}>Página {currentPage} de {totalPages}</span>
                <button
                  className={styles.pageButton}
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CatalogPage;
